/*
  XMARLIN — <xm-carousel>, the lifestyle carousel in the editorial hero.

  No framework, no dependencies, one file — the same contract as
  assets/xm-product.js.

  The track is a native CSS scroll-snap strip. That is deliberate: the browser
  already owns momentum, touch, trackpad, rubber-banding and the reduced-motion
  preference, and every one of those is a bug waiting to happen in hand-written
  transform code. This file only does the three things the browser does NOT:
  drive the arrows and dots, keep the dots in step with wherever the strip
  actually is, and run the timer.

  Behaviour worth knowing before changing it:

    - `scrollIntoView` is NOT used. It scrolls every scrollable ancestor,
      which on this page means the document jumps to the hero when the timer
      advances a slide a user has scrolled past. The track is scrolled
      directly by `scrollLeft`, which cannot move anything else.
    - Autoplay stops for good on the first arrow, dot or key press. Pausing on
      hover alone loses a fight with a person who is trying to look at slide 4,
      because the timer restarts the moment the pointer leaves.
    - `prefers-reduced-motion` turns off the timer AND the smooth behaviour.
      A carousel that moves on its own is the exact thing that preference is
      about.
    - The index is READ BACK from `scrollLeft` rather than held in a variable,
      so a swipe, a keyboard scroll and a click all converge on one source of
      truth and the dots cannot drift out of step.
*/
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class XmCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-xmc-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-xmc-slide]'));
      if (this.slides.length < 2) {
        // One slide is a picture, not a carousel. Hide the furniture rather
        // than render a "1 / 1" counter and two dead arrows.
        this.querySelectorAll('[data-xmc-chrome]').forEach((el) => el.remove());
        return;
      }

      this.dots = Array.from(this.querySelectorAll('[data-xmc-dot]'));
      this.liveEl = this.querySelector('[data-xmc-live]');
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && !REDUCED.matches;
      this.interval = parseInt(this.dataset.interval, 10) || 5500;

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      const prev = this.querySelector('[data-xmc-prev]');
      const next = this.querySelector('[data-xmc-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1, true));
      if (next) next.addEventListener('click', () => this.step(1, true));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i, true));
      });

      // Arrow keys work on the track itself because it is a focusable region.
      // Left/Right are flipped under RTL so the keys still mean "previous"
      // and "next" rather than "lower scrollLeft" and "higher".
      this.track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const forward = rtl ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
        e.preventDefault();
        this.step(forward ? 1 : -1, true);
      });

      if (this.autoplay) {
        this.addEventListener('pointerenter', () => this.pause());
        this.addEventListener('pointerleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.resume());
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.pause();
          else this.resume();
        });
        // A carousel running off-screen is timer work nobody can see, and it
        // means a visitor who scrolls back up lands on a random slide.
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.resume();
              else this.pause();
            });
          }, { threshold: 0.35 });
          this.io.observe(this);
        } else {
          this.resume();
        }
      }

      this.sync();
    }

    disconnectedCallback() {
      this.pause();
      if (this.io) this.io.disconnect();
    }

    /* ---------------------------------------------------------- movement */

    // Slide width rather than a cached number: the track is fluid, and a
    // resize between load and click would otherwise land between two slides.
    slideWidth() {
      return this.slides[0].getBoundingClientRect().width;
    }

    go(i, manual) {
      const n = this.slides.length;
      const target = ((i % n) + n) % n;
      // scrollLeft is negative under RTL in every engine that matters now.
      const dir = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.track.scrollTo({
        left: dir * target * this.slideWidth(),
        behavior: REDUCED.matches ? 'auto' : 'smooth',
      });
      if (manual) this.stopForGood();
    }

    step(delta, manual) {
      this.go(this.index + delta, manual);
    }

    /* ------------------------------------------------------------- state */

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.sync();
      });
    }

    sync() {
      const w = this.slideWidth();
      if (!w) return;
      const i = Math.round(Math.abs(this.track.scrollLeft) / w);
      const clamped = Math.max(0, Math.min(this.slides.length - 1, i));
      if (clamped === this.index && this.synced) return;
      this.index = clamped;
      this.synced = true;

      this.dots.forEach((dot, d) => {
        const on = d === clamped;
        dot.setAttribute('aria-current', on ? 'true' : 'false');
        dot.classList.toggle('is-on', on);
      });

      this.slides.forEach((slide, s) => {
        // A slide scrolled out of view is still in the accessibility tree and
        // its link is still tabbable, which is how a keyboard user ends up
        // focused on something they cannot see. inert is the one attribute
        // that takes the whole subtree out of both.
        if (s === clamped) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });

      if (this.liveEl) {
        this.liveEl.textContent = this.liveEl.dataset.template
          .replace('@i', String(clamped + 1))
          .replace('@n', String(this.slides.length));
      }
    }

    /* ----------------------------------------------------------- autoplay */

    resume() {
      if (!this.autoplay || this.timer || this.stopped) return;
      this.timer = setInterval(() => this.step(1, false), this.interval);
    }

    pause() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    stopForGood() {
      this.stopped = true;
      this.pause();
    }
  }

  if (!customElements.get('xm-carousel')) {
    customElements.define('xm-carousel', XmCarousel);
  }
})();
